// Generated by scripts/build-rules-content.mjs from country-es-mx-cz.json (aligned 9 Oct 2026).
// Content for sales: plain sentences, no em dash, no person or company names.
// Edit here or regenerate; tests/rules-explainer.test.mjs checks the writing rules.
import type { CountryGuide } from "../types";

export const GUIDE_SPAIN_MEXICO_CZECHIA: CountryGuide = {
  id: "spain-mexico-czechia",
  code: "es-mx-cz",
  title: "Spain, Mexico and Czechia",
  shortName: "Spain, Mexico and Czechia",
  entities: ["es", "mx", "cz"],
  areaRoles: ["es", "mx", "cz"],
  live: true,
  notLiveNote:
    "Spain is live in I2Q. Czechia is live in shadow mode, with three gaps: no item group mapping, prices from EUR price groups while freight is in CZK, and no reading of replies. Mexico is not switched on yet: its mails are read, but get no matched items and no prices.",
  cardLine:
    "Spain is live, Czechia live in shadow mode with gaps. Mexico is read, but not matched or priced yet.",
  notYet: [
    "Mexico: inquiries are read but not matched to items or priced, because the item catalogue is not loaded yet.",
    "Czechia: no item group mapping, prices from EUR price groups while freight is in CZK, and I2Q does not read the replies, so an answered inquiry stays open until someone closes it.",
    "Lead times are not available for Spanish, Mexican and Czech lines yet: the line shows no delivery date.",
  ],
  summary:
    "Spain (Wheelabrator Group S.L.U.) is one of the six areas Norican I2Q has worked on since the start. Czechia was switched on in shadow mode. Mexico is built in I2Q but still switched off: its item catalogue is not loaded yet, so its inquiries are not matched to items or priced. In all three countries I2Q only prepares a draft. Sales always checks, decides and sends. In Go Autonomous, the tool used before I2Q, Spain made almost no quotes (0 to 5 %): one user, no backup and no time for training. Mexico and Czechia never went live in Go Autonomous.",
  howInquiriesArrive: [
    "Spain: customer mail arrives in the shared mailbox Info.ES (website inquiries; the old wheelabratorgroup.es address forwards into it). I2Q reads a copy of it. The service mailbox INFOSAT also belongs to Spain, and only people with the Spain area see it.",
    "Mexico: the shared mailbox Ventas.MX (being renamed Norican.Parts.MX); I2Q reads a copy of it.",
    "Czechia: the shared mailbox prodej, created in May 2025 and never promoted to customers; I2Q reads a copy of it. Czechia mails are matched and priced; Mexico mails are read, but get no item matching until Mexico is switched on. I2Q always takes the country from the mailbox the mail arrived in, never from the address the customer typed.",
  ],
  specialities: [
    {
      id: "es-mx-cz-area-status",
      title: "Czechia is live with gaps, Mexico is not switched on yet",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks: "Whether the country's item catalogue is loaded and the area is switched on in I2Q.",
      why: "In Go Autonomous only about half of the UK item data was in the live system at go-live, so I2Q waits until a country's catalogue is loaded and checked.",
      youSee:
        "Info. Spain and Czechia: the full draft (customer, items, prices, reply text). In Czechia check the currency: price groups are in EUR, freight is in CZK. Mexico: the mail is read and stored, but it gets no matched items and no prices until the area is switched on.",
      whatToDo:
        "In Czechia, check every quotation line by line and close the case in I2Q when you answered it, because I2Q does not see Czech replies yet. In Mexico, quote as you do today.",
      example:
        "A Czech foundry asks prodej for three wear parts. Today I2Q keeps the mail and leaves the lines for you to quote by hand.",
      registryIds: [],
      glossaryIds: [],
      source:
        "areas.py (mx and cz are new areas, off until switched on); live-data-concept.md (three switches per country); sales-rep-review-and-roadmap.md item 11 and step 3.1",
    },
    {
      id: "es-infosat-service",
      title: "INFOSAT is a service mailbox, not parts sales",
      status: "active",
      tone: "info",
      topic: "items",
      checks: "Whether a Spanish mail came in through the service mailbox INFOSAT.",
      why: "In Go Autonomous parts for a service visit needed a different sales product and sales pool, which had to be set by hand.",
      youSee:
        'Info. The case is in the Spain area and marked as coming from the service mailbox. A line in a service item group gets a warning: "Line 2 is a service item in group ..., not a spare part."',
      whatToDo:
        "Treat it as a service request. If parts are quoted for a service visit, set the sales pool and sales product for service yourself in F&O.",
      example:
        "A customer writes to INFOSAT after a technician's visit and asks for the parts the technician listed. I2Q matches the parts. You set the service pool when you create the quotation.",
      registryIds: ["gate.service_visit"],
      glossaryIds: ["service-visit-parts"],
      modes: {
        "gate.service_visit": {
          es: "warn",
          mx: "warn",
          cz: "warn",
        },
      },
      source:
        "areas.py (INFOSAT = service mailbox of es); live-data-concept.md (INFOSAT visible with Area.ES); goa-knowledge.md 9.1 (#22, #61 service-visit parts)",
    },
    {
      id: "es-mx-cz-customer-per-country",
      title: "A customer number means a different firm in each country",
      status: "active",
      tone: "info",
      topic: "customer",
      checks:
        "That purchase history, earlier quotations and customer part numbers come only from the country of the mailbox.",
      why: "Customer numbers are counted separately in every country, and most Mexican account numbers (409 of 438) belong to a different company in another country.",
      youSee:
        'Info. Customer suggestions and "has bought before" hints only use history from your own country.',
      whatToDo:
        "Nothing extra. If a Mexican customer is known in North America, look it up there yourself; I2Q will not mix the two.",
      example:
        "The same account number belongs to a die-caster in Mexico and to a different company in North America. I2Q uses only the Mexican history for the Mexican mail.",
      registryIds: [],
      glossaryIds: ["customer-finding"],
      source: "live-data-concept.md step 6 (history read in the case's own country only)",
    },
    {
      id: "es-cz-few-contacts",
      title: "Few contacts with an e-mail address",
      status: "active",
      tone: "warning",
      topic: "customer",
      checks:
        "Whether the sender's e-mail address belongs to a contact of a customer account in F&O.",
      why: "A sender is recognised through the contact's e-mail address, and in Go Autonomous Spain had only 19 such contacts and Czechia 148.",
      youSee:
        'Warning: the customer is often shown as "Suggested, please confirm" instead of certain.',
      whatToDo:
        "Confirm or change the customer before you look at prices. Add the e-mail address to the contact in F&O, so the next mail is recognised.",
      example:
        "A buyer at a Spanish steel plant writes from a new address. I2Q suggests the account from the company domain and asks you to confirm it.",
      registryIds: [],
      glossaryIds: ["customer-finding"],
      source: "goa-knowledge.md 2.1 (contacts with e-mail: CZ 148, ES 19)",
    },
    {
      id: "es-agents",
      title: "Spain: agents and distributors",
      status: "planned",
      tone: "info",
      topic: "customer",
      checks:
        "Whether the mail comes from an agent or distributor writing for an end customer, and which account the quotation belongs to.",
      why: "In Go Autonomous a Portuguese agent was taken as the customer itself.",
      youSee:
        'Today: a suggestion "Earlier documents of the agent" when the agent\'s earlier quotations point to one customer. Planned: a list of Spanish agent domains (for example agents in Portugal and Italy) and a clear end-customer rule.',
      whatToDo:
        "Make the quotation on the end customer's account and send it to the agent. Sometimes the goods go straight to the end customer; agree that with your manager.",
      example:
        "An agent in Portugal forwards a request from a Portuguese foundry. You confirm the foundry as the customer and send the quotation back to the agent.",
      registryIds: [],
      glossaryIds: ["agents-and-distributors"],
      source:
        "goa-knowledge.md 3 Spain (agent sales units a Spanish agent, and three more); part B 3 Customer (a Spanish agent exception, OW1 [02:15]); 9.3 agents, four cases; eligibility-rules-concept.md (ES default: quote the writer)",
    },
    {
      id: "es-competitor-customers",
      title: "Competitors who are also customers",
      status: "active",
      tone: "warning",
      topic: "customer",
      checks:
        "Whether the sender's domain is on the group list of competitors, and whether that competitor is also a customer in Spain.",
      why: "Norican quotes competitors higher or not at all, but in Spain five competitors are also customers, so a hard block would stop real business.",
      youSee:
        'For the five competitors that are also customers in Spain: a warning, "... but this company is also a customer of this entity." For any other competitor: blocked, "The sender\'s domain is on the competitor list."',
      whatToDo:
        "Check that this is a genuine customer inquiry before you quote. For other competitors, quote only if your manager agrees.",
      example:
        "A shot blast company that competes with us on machines and buys spare parts asks for prices. The case shows the warning, and you quote at the agreed price.",
      registryIds: ["gate.competitor"],
      glossaryIds: ["competitor-domains"],
      modes: {
        "gate.competitor": {
          es: "block",
          mx: "block",
          cz: "block",
        },
      },
      source:
        "goa-knowledge.md 2.1 Competitor domains (ES: 5 competitors are customers; GoA bug 43)",
    },
    {
      id: "es-mx-cz-sanctioned",
      title: "Sanctioned countries",
      status: "active",
      tone: "blocked",
      topic: "customer",
      checks:
        "Whether the sender writes from a country under sanctions (for example .ru, .by, .ir, .sy, .cu, .kp addresses).",
      why: "Go Autonomous sent these mails to manual handling, because Norican must not quote into sanctioned countries.",
      youSee:
        'Blocked: "The sender\'s mail domain belongs to Russia, which is on the sanctions list." The case is held.',
      whatToDo: "Do not quote. Pass the inquiry to your manager for an export control check.",
      example: "A request arrives from a .ru address. The case is held with the sanctions reason.",
      registryIds: ["gate.sanctioned"],
      glossaryIds: ["sanctioned-countries"],
      modes: {
        "gate.sanctioned": {
          es: "block",
          mx: "block",
          cz: "block",
        },
      },
      source: "goa-knowledge.md 2.1 Sanctioned countries (blacklisted countries.xlsx)",
    },
    {
      id: "es-mx-cz-price",
      title: "Where the price comes from",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks:
        "The price from the customer's price list in F&O (F&O calls it a trade agreement). First a price list just for this customer, else the customer's price group, valid on the day and for the quantity asked.",
      why: "Go Autonomous showed that a missing or old price must never be guessed, so a line without a price went to manual handling.",
      youSee:
        'Spain: the price on each matched line. Blocked: the line shows "No price", and the prepared quotation says "No price was found for ...". The other lines still show. Mexico and Czechia: once their area is switched on.',
      whatToDo:
        "Check the price. If there is none, ask for a price as you do today; do not type a guessed price into the draft.",
      example:
        "A Spanish customer in price group EU-100 asks for 20 blast wheel blades. I2Q fills in the EU-100 price for 20 pieces.",
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price"],
      modes: {
        "pricing.no_price": {
          es: "block",
          mx: "block",
          cz: "block",
        },
      },
      source: "goa-knowledge.md 2.3 Price source, No price; pricing/lookup.py",
    },
    {
      id: "es-mx-cz-margin",
      title: "Margin check",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks:
        "Where F&O has a cost for the item, how much of the price is left after cost (the contribution ratio). It is compared with the margin limit of your country, which sales management sets.",
      why: "Go Autonomous stopped low-margin quotations, and sales asked for a step that a manager can release instead.",
      youSee:
        'Blocked until a manager approves: the line shows "Margin: needs approval". If F&O has no cost for the item, the line shows "Margin: not checked (no cost in F&O)", and the prepared quotation is held as well. Only colleagues with margin rights see the figures. Everyone else sees the result only.',
      whatToDo:
        'Check the price. If it is right, ask your manager to release it with "Approve price". If the item, price, quantity or customer changes later, the approval must be given again.',
      example:
        'A cylinder for a Spanish customer comes out below the margin limit. The line shows "Margin: needs approval" until your manager approves the price.',
      registryIds: ["pricing.margin"],
      glossaryIds: ["contribution-ratio"],
      modes: {
        "pricing.margin": {
          es: "block",
          mx: "block",
          cz: "block",
        },
      },
      source:
        "pricing/thresholds.py (es, mx, cz = 0.40); goa-knowledge.md 2.3 Contribution ratio gate",
    },
    {
      id: "mx-customer-discount",
      title: "Mexico: customer discount only on Wheelabrator parts",
      status: "planned",
      tone: "warning",
      topic: "price",
      checks: "Whether a customer discount from the price list lands on a DISA or Simpson line.",
      why: "In North America, Canada and Mexico customer discounts are meant for Wheelabrator parts only, but since the merge they also land on DISA and Simpson lines.",
      youSee:
        "Planned: a warning on the DISA or Simpson line that still carries a customer discount.",
      whatToDo: "Remove the discount from that line before you send.",
      example:
        "A Mexican customer has 10 % off. The quotation has two Wheelabrator lines and one DISA line. I2Q will flag the DISA line.",
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md 2.3 Customer discounts (USL, CA, MX; Issue Log #37)",
    },
    {
      id: "es-freight",
      title: "Spain: freight by order value",
      status: "active",
      tone: "info",
      topic: "freight",
      checks:
        "The freight line on the quotation, by order value in EUR (charge code FR_Auto). Up to 500: 35. 500 to 2,500: 75. 2,500 to 5,000: 150. 5,000 to 10,000: 175. 10,000 to 20,000: 300. 20,000 to 50,000: 400.",
      why: "In Go Autonomous freight decided adoption: France did not use the tool for months because its freight exception was not set up, and Spain uses the same table.",
      youSee:
        'Info in the preview: "Freight of 75 EUR is proposed for an order value of 500.01 to 2000 EUR." Warning "Set by hand" above 50,000 EUR or with a Media line.',
      whatToDo:
        "Check the freight line before the quotation goes out. Remove it when the customer's delivery terms exclude freight (pick-up or a special agreement).",
      example: "A Spanish order value of 1,200 EUR gets a freight line of 75 EUR.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          es: "warn",
          mx: "warn",
          cz: "warn",
        },
      },
      source:
        "goa-knowledge.md 2.5 Freight tiers (Freight Charges Wheelabrator Europe v3.xlsx); 9.3 freight models; 2.2 Media item groups",
    },
    {
      id: "cz-freight",
      title: "Czechia: freight by order value in CZK",
      status: "active",
      tone: "info",
      topic: "freight",
      checks:
        "The freight line in CZK by order value, from the same European table. For example 861.64 CZK up to the first band.",
      why: "Czechia is meant to use freight by order value like the other European companies, but one value in the CZK table looks wrong.",
      youSee:
        "Once Czechia is switched on, the preview proposes freight in CZK from the table as it stands. The table has one odd value, so check the amount.",
      whatToDo: "Add freight by hand as today. Tell sales management if the table is wrong.",
      example:
        "A Czech quotation with an order value in the 500 to 2,000 band gets 1,846.37 CZK freight.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          es: "warn",
          mx: "warn",
          cz: "warn",
        },
      },
      source: "goa-knowledge.md 2.5 Freight tiers (CZ values, source error note)",
    },
    {
      id: "mx-payment-terms",
      title: "Mexico: payment terms and prepayment",
      status: "planned",
      tone: "warning",
      topic: "freight",
      checks:
        "The customer's payment terms from F&O. Mexico uses N5 to N90, CIA (cash in advance), COD, PROFORMA and TPC.",
      why: "Customers who pay in advance need a pro-forma with freight before the order, and Go Autonomous left these to people.",
      youSee:
        "Planned: payment terms on the quotation preview, and a warning for prepayment terms (CIA, PROFORMA).",
      whatToDo:
        "For prepayment customers, get the freight cost from shipping and make a pro-forma by hand.",
      example:
        "A Mexican customer with term CIA asks for parts. I2Q will warn that freight must be quoted up front.",
      registryIds: [],
      glossaryIds: ["freight"],
      source:
        "goa-knowledge.md 3 Czech Republic, Mexico (NA Payment Terms.xlsx); 2.5 Payment terms; 9.3 freight model (c)",
    },
    {
      id: "es-mx-cz-tax-groups",
      title: "Tax and customer group",
      status: "planned",
      tone: "info",
      topic: "tax",
      checks:
        "The customer group from F&O: in Spain EU, EXT (outside the EU), IC (sister company) or LOCAL. It tells whether the quotation is domestic, export or intercompany.",
      why: "Go Autonomous had export and tax rules that never worked because the data was missing on the customer account.",
      youSee:
        "Planned: a hint when the customer is export or a sister company. Tax itself is taken from the customer and the delivery address in F&O.",
      whatToDo: "Check the delivery address and tax on export quotations before you send.",
      example:
        "A customer in Morocco (group EXT) asks Info.ES for parts. I2Q will mark it as export.",
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md 2.1 Customer groups (ES EU/EXT/IC/LOCAL); part B 3 Tax",
    },
    {
      id: "cz-sister-companies",
      title: "Czechia supplies the other companies",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Whether a mail to Czechia or about Czech items comes from a sister company (UK, France and others order or ask prices from Czechia).",
      why: "In Go Autonomous business between Norican companies stayed on the old order system, and the tool was only meant for Czechia's direct customers.",
      youSee:
        'Info. The sender is shown as "Sister company". Intercompany customers start with 29.',
      whatToDo:
        "Answer sister-company price requests as you do today. Use I2Q drafts for direct customers.",
      example:
        'The UK inside sales team asks prodej for a price on a non-stock item. The case shows "Sister company". Lead time for Czech non-stock items to the UK is about 27 working days plus 5 to 7 days transport.',
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md 3 Czech Republic (check list for other entities); 2.3 Lead time; 5 Numbering (intercompany starts with 29)",
    },
    {
      id: "es-mx-cz-language",
      title: "Language of the reply and the quotation",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "The reply text follows the language of the customer's mail. The quotation document should use the customer's language, else the country default: Spain es, Mexico es-mx, Czechia en-us.",
      why: "Until April 2025 every company printed in US English, and customers got quotations in the wrong language.",
      youSee:
        "Info: the reply text is in the customer's language. The preview shows the document language: the customer's, else the country default.",
      whatToDo: "Check the language field in F&O when you create the quotation.",
      example:
        "A Mexican customer writes in Spanish. The reply draft is in Spanish; you set the quotation language to es-mx.",
      registryIds: [],
      glossaryIds: ["translation"],
      source: "goa-knowledge.md 2.5 Quote document language, Reply language; 9.1 (#28)",
    },
    {
      id: "es-validity",
      title: "Spain: no validity in the reply",
      status: "planned",
      tone: "info",
      topic: "quotation",
      checks: "How long the quotation is valid.",
      why: "The reply templates of the other countries say 30 days, but the Spanish one gives no validity at all.",
      youSee: "Planned: the validity date on the quotation preview.",
      whatToDo: "Write the validity into your reply until Spain has agreed a standard.",
      example: 'You add "Valid for 30 days" to the Spanish reply draft before sending.',
      registryIds: [],
      glossaryIds: ["quotation-validity"],
      source: "goa-knowledge.md 2.5 Validity / expiry; 3 Spain (Quote reply template ES.docx)",
    },
    {
      id: "es-header-fields",
      title: "Spain: header fields of the quotation",
      status: "active",
      tone: "warning",
      topic: "quotation",
      checks:
        "The fields at the top of the F&O quotation. Sales pool from the item group with the most lines (PART-AIR, PART-WHE, PART-DOS, PART-MEL or AST-WHE). Division, business type and sales product. Sales unit (Spain has 19, including agent units). Customer reference: the customer's inquiry number. And the Sales Origin value that marks quotations prepared with a tool.",
      why: "In Go Autonomous a missing Sales Origin value in the Spanish live system made quotation creation fail.",
      youSee:
        "Spain: the preview proposes the sales pool, the sales fields and the customer reference. Mexico and Czechia have no item group mapping yet, so they get a warning and no pool.",
      whatToDo:
        "Fill the sales pool and check the sales unit in F&O when you create the quotation.",
      example:
        "A Spanish inquiry has three Wheelabrator lines and one DISA line. The pool will be PART-WHE, because Wheelabrator has the most lines.",
      plannedPart: "Sales unit and the Sales Origin value on the preview.",
      registryIds: ["header.pool", "header.not_available"],
      glossaryIds: ["quotation-header", "customer-reference-fields"],
      modes: {
        "header.pool": {
          es: "warn",
          mx: "warn",
          cz: "warn",
        },
        "header.not_available": {
          es: "info",
          mx: "info",
          cz: "info",
        },
      },
      source:
        "goa-knowledge.md 2.4 (ES pools, ES 19 sales units); 9.1 (#112 Sales Origin missing in ES Live); 5 Header fields",
    },
    {
      id: "es-urgency",
      title: "Urgent and machine-down mails",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks: 'Spanish words for "urgent" and "machine down" in the mail.',
      why: 'Go Autonomous flagged too many mails as urgent, for example because UK order forms print "next day delivery" on every order.',
      youSee: 'Planned: an "urgent" tag on the case.',
      whatToDo:
        "Look at tagged cases first, but read the mail before you change your order of work.",
      example: '"Máquina parada, necesitamos la pieza urgente" will get the tag.',
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md 2.5 Urgency tags (EN/FR/ES list)",
    },
  ],
};
